import { FunctionComponent } from 'react'
import styles from './mobileNav.module.scss'
import FamiliesIcon from '@mui/icons-material/SupervisedUserCircle'
import { Role } from '@/types/Role'
import { Daycare, DaycareType } from '@/types/Daycare'
import { Location, useLocation } from 'react-router-dom'
import { CalendarMonth, ChildCareOutlined, Home, MenuOpen, Sms } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { daycareTypeToString } from '@/modules/helper/daycareHelper'
import ButtonLink from '../ButtonLink/buttonLink'
import MenuIcon from '@mui/icons-material/Menu'

interface MobileNavProps {
    userRole: Role
    newsCount: number
    canReadContracts: boolean
    canReadFamilies: boolean
    canReadNews: boolean
    hasNewsAccess: boolean
    daycare?: Daycare
    daycaresLength: number
}

const MobileNav: FunctionComponent<MobileNavProps> = ({
    userRole,
    newsCount,
    canReadFamilies,
    canReadContracts,
    canReadNews,
    hasNewsAccess,
    daycare,
    daycaresLength
}: MobileNavProps) => {
    const { t } = useTranslation()

    const location: Location = useLocation()

    const daycareTypeName: string =
        daycaresLength === 1 && daycare ? daycareTypeToString(daycare.type, t, true) : t('structures')

    const homeIconActive: boolean =
        location.pathname === '/home' ||
        location.pathname === '/settings' ||
        location.pathname === '/account/notifications' ||
        location.pathname === '/account/profile' ||
        (location.pathname === '/account/childs' && hasNewsAccess) ||
        location.pathname === '/group' ||
        location.pathname === '/suggest' ||
        location.pathname === '/staff' ||
        location.pathname === '/taskNote' ||
        location.pathname === '/meal' ||
        ((location.pathname === '/adults' || location.pathname === '/childs') && hasNewsAccess && canReadNews)

    const isRpeOrLaep: boolean = daycare?.type === DaycareType.rpe || daycare?.type === DaycareType.laep

    return (
        <div className={styles.MobileNav}>
            <div className={styles.NavMenu}>
                <div className={styles.ButtonLink}>
                    <ButtonLink
                        href='/daycare'
                        locationActive={location.pathname === '/daycare'}
                        role={userRole}
                        icon={<Home className={styles.Icon} />}
                        text={daycareTypeName}
                        isMobile
                    />
                </div>
                {daycare && (
                    <div className={styles.ButtonLink}>
                        <ButtonLink
                            href='/calendar'
                            locationActive={location.pathname === '/calendar'}
                            role={userRole}
                            icon={<CalendarMonth className={styles.Icon} />}
                            text={t('planning')}
                            isMobile
                        />
                    </div>
                )}
                {hasNewsAccess && canReadNews ? (
                    <div className={styles.ButtonLink}>
                        <ButtonLink
                            href='/news'
                            locationActive={location.pathname.startsWith('/news')}
                            role={userRole}
                            icon={<Sms className={styles.Icon} />}
                            text={t('news')}
                            badge={userRole === Role.parent ? newsCount : 0}
                            isMobile
                        />
                    </div>
                ) : (canReadContracts || canReadFamilies) && canReadFamilies ? (
                    <div className={styles.ButtonLink}>
                        <ButtonLink
                            href={isRpeOrLaep ? '/adults' : canReadContracts ? '/childs' : '/adults'}
                            locationActive={location.pathname === '/childs' || location.pathname === '/adults'}
                            role={userRole}
                            icon={<FamiliesIcon className={styles.Icon} />}
                            text={isRpeOrLaep ? t('publics') : t('families')}
                            isMobile
                        />
                    </div>
                ) : (
                    userRole === Role.parent && (
                        <div className={styles.ButtonLink}>
                            <ButtonLink
                                href={'/account/childs'}
                                locationActive={location.pathname === '/account/childs'}
                                role={userRole}
                                icon={<ChildCareOutlined className={styles.Icon} />}
                                text={t('myChilds')}
                                isMobile
                            />
                        </div>
                    )
                )}
                <div className={styles.ButtonLink}>
                    <ButtonLink
                        href={'/home'}
                        locationActive={homeIconActive}
                        role={userRole}
                        icon={
                            homeIconActive ? <MenuOpen className={styles.Icon} /> : <MenuIcon className={styles.Icon} />
                        }
                        text={t('menu')}
                        isMobile
                    />
                </div>
            </div>
        </div>
    )
}

export default MobileNav
